import { CircleCheck as CheckCircle, Clock, MessageCircle, PhoneIncoming, BellRing, Ban } from 'lucide-react';

interface Props {
  name: string;
  phone: string;
}

const STEPS = [
  { icon: CheckCircle, title: 'Cadastro recebido', text: 'Seus dados chegaram para a nossa equipe.', state: 'done' },
  { icon: Clock, title: 'Aguardando contato', text: 'Um vendedor vai chamar você o mais rápido possível.', state: 'current' },
  { icon: MessageCircle, title: 'Atendimento pelo WhatsApp', text: 'Você recebe todas as informações diretamente com o vendedor.', state: 'next' },
] as const;

export default function LeadSuccessScreen({ name, phone }: Props) {
  const firstName = name.trim().split(/\s+/)[0] || '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white px-4 py-10">
      <div className="mx-auto w-full max-w-md">
        <div className="text-center">
          <div className="relative mx-auto mb-6 h-20 w-20">
            <span className="absolute inset-0 animate-ping rounded-full bg-emerald-200 opacity-60" />
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-emerald-600 shadow-lg shadow-emerald-200">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold leading-tight text-slate-900">
            {firstName ? `Pronto, ${firstName}!` : 'Pronto!'} Cadastro recebido.
          </h1>
          <p className="mt-2 text-base leading-6 text-slate-600">Agora você só precisa aguardar.</p>
        </div>

        <div className="mt-8 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 shadow-sm">
          <div className="flex gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-400">
              <PhoneIncoming className="h-6 w-6 text-amber-950" />
            </div>
            <div>
              <p className="text-lg font-bold leading-tight text-amber-950">Aguarde, vamos chamar você</p>
              <p className="mt-1 text-sm leading-6 text-amber-900">
                Um de nossos vendedores vai entrar em contato pelo WhatsApp o mais rápido possível.
              </p>
              {phone && (
                <p className="mt-3 inline-flex rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-amber-950 ring-1 ring-amber-200">
                  {phone}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-2xl bg-white p-6 shadow-lg shadow-slate-200/60 ring-1 ring-slate-100">
          <p className="mb-5 text-xs font-semibold uppercase tracking-wider text-slate-400">Próximos passos</p>
          <ol>
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const last = index === STEPS.length - 1;
              return (
                <li key={step.title} className="relative flex gap-4 pb-6 last:pb-0">
                  {!last && (
                    <span className={`absolute left-5 top-10 h-[calc(100%-2.5rem)] w-0.5 ${step.state === 'done' ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                  )}
                  <div
                    className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                      step.state === 'done'
                        ? 'bg-emerald-600 text-white'
                        : step.state === 'current'
                          ? 'bg-amber-100 text-amber-700 ring-4 ring-amber-50'
                          : 'bg-slate-100 text-slate-400'
                    }`}
                  >
                    {step.state === 'current' && <span className="absolute inset-0 animate-pulse rounded-full bg-amber-200/60" />}
                    <Icon className="relative h-5 w-5" />
                  </div>
                  <div className="pt-1">
                    <p className={`font-semibold ${step.state === 'next' ? 'text-slate-500' : 'text-slate-900'}`}>
                      {step.title}
                      {step.state === 'current' && (
                        <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Agora</span>
                      )}
                    </p>
                    <p className="mt-0.5 text-sm leading-6 text-slate-500">{step.text}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="mt-6 space-y-3">
          <div className="flex items-start gap-3 rounded-xl bg-white p-4 ring-1 ring-slate-100">
            <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <p className="text-sm leading-6 text-slate-600">
              Fique de olho no seu WhatsApp e atenda mesmo que a mensagem venha de um número que você não conhece.
            </p>
          </div>
          <div className="flex items-start gap-3 rounded-xl bg-white p-4 ring-1 ring-slate-100">
            <Ban className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
            <p className="text-sm leading-6 text-slate-600">
              Não é preciso preencher o cadastro novamente. Seus dados já estão com a nossa equipe.
            </p>
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-slate-400">Você já pode fechar esta página.</p>
      </div>
    </div>
  );
}
